/**
 * QRCodeScanner - Standalone QR Code Scanner Component
 * 
 * A reusable QR code scanner that can be used across the entire application
 * by any role (patients, professionals, ChartMind users, etc.)
 * 
 * Features:
 * - HTML5-based camera scanning (html5-qrcode)
 * - Manual code entry fallback
 * - Cross-browser compatible
 * - Handles permissions gracefully
 * - Dev mode with mock data support
 * 
 * Usage:
 * ```jsx
 * import QRCodeScanner from 'components/common/QRCodeScanner';
 * 
 * <QRCodeScanner
 *   open={scannerOpen}
 *   onClose={() => setScannerOpen(false)}
 *   onScanSuccess={(data) => console.log('Scanned:', data)}
 *   title="Scan QR Code"
 *   description="Point your camera at the QR code"
 * />
 * ```
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  QrCodeScanner,
  Close,
  CheckCircle,
  ErrorOutline,
  Refresh,
  ContentPaste,
  CameraAlt,
  Keyboard,
} from '@mui/icons-material';
import { QRScanner, isCameraSupported } from '@healthdesk/shared-utils';

// Unique ID for the QR scanner element
const QR_SCANNER_ELEMENT_ID = 'universal-qr-scanner';

/**
 * QRCodeScanner Component
 * 
 * @param {boolean} open - Whether the scanner dialog is open
 * @param {function} onClose - Called when scanner is closed
 * @param {function} onScanSuccess - Called with decoded data when QR code is successfully scanned
 * @param {function} onScanError - Optional callback for scan errors
 * @param {string} title - Dialog title (default: "Scan QR Code")
 * @param {string} description - Description text (default: "Point your camera at the QR code")
 * @param {boolean} allowManualEntry - Whether to show manual code entry option (default: true)
 * @param {string} manualEntryLabel - Label for manual entry field (default: "Manual Code")
 * @param {function} validateManualCode - Optional validator for manual codes (returns {valid: boolean, error?: string})
 * @param {boolean} showDevMode - Whether to show dev mode options (default: auto-detect)
 * @param {React.ReactNode} devModeContent - Optional custom content for dev mode tab
 */
const QRCodeScanner = ({
  open = false,
  onClose,
  onScanSuccess,
  onScanError,
  title = 'Scan QR Code',
  description = 'Point your camera at the QR code to scan',
  allowManualEntry = true,
  manualEntryLabel = 'Manual Code',
  validateManualCode,
  showDevMode,
  devModeContent,
}) => {
  const scannerRef = useRef(null);
  
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cameraSupported, setCameraSupported] = useState(true);
  
  // Tab state (0: camera, 1: manual entry, 2: dev mode)
  const [activeTab, setActiveTab] = useState(0);
  const [manualCodeInput, setManualCodeInput] = useState('');

  // Auto-detect dev mode
  const isDevMode = showDevMode !== undefined ? showDevMode : (() => {
    if (typeof window === 'undefined') return false;
    const hostname = window.location.hostname;
    return hostname === 'localhost' || 
           hostname === '127.0.0.1' || 
           hostname.includes('experimental');
  })();

  // Check camera support on mount
  useEffect(() => {
    setCameraSupported(isCameraSupported());
  }, []);

  // Start camera when dialog opens and camera tab is active
  useEffect(() => {
    if (open && activeTab === 0 && cameraSupported && !scanning && !success) {
      startCamera();
    }
    
    // Cleanup on unmount or close
    return () => {
      if (scannerRef.current) {
        stopCamera();
      }
    };
  }, [open, activeTab, cameraSupported]);

  // Handle QR code detection
  const handleQRDetected = useCallback(async (decodedData) => {
    
    // Stop scanner immediately to prevent multiple scans
    stopCamera();
    setLoading(true);
    setError(null);
    
    try {
      // Call success callback
      await onScanSuccess?.(decodedData);
      
      setSuccess(true);
      setLoading(false);
      
      // Auto-close after brief delay
      setTimeout(() => {
        handleClose();
      }, 1500);
      
    } catch (err) {
      console.error('[QRCodeScanner] Error processing QR code:', err);
      setError(err.message || 'Failed to process QR code');
      setLoading(false);
      
      // Call error callback if provided
      onScanError?.(err);
    }
  }, [onScanSuccess, onScanError]);

  // Start camera scanning
  const startCamera = async () => {
    if (scanning || !cameraSupported) return;
    
    setScanning(true);
    setError(null);
    setSuccess(false);
    
    try {
      scannerRef.current = new QRScanner(QR_SCANNER_ELEMENT_ID);
      
      await scannerRef.current.start({
        onSuccess: handleQRDetected,
        onError: (errorInfo) => {
          console.error('[QRCodeScanner] Scanner error:', errorInfo);
          
          if (errorInfo.type === 'camera_error') {
            setError(errorInfo.message);
            setScanning(false);
          }
        },
        preferBackCamera: true,
      });
      
    } catch (err) {
      console.error('[QRCodeScanner] Failed to start camera:', err);
      setError('Failed to access camera. Please check permissions.');
      setScanning(false);
    }
  };

  // Stop camera scanning
  const stopCamera = () => {
    if (scannerRef.current) {
      try {
        scannerRef.current.stop();
      } catch (err) {
        console.warn('[QRCodeScanner] Error stopping scanner:', err);
      }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  // Handle manual code entry
  const handleManualCodeSubmit = async () => {
    if (!manualCodeInput.trim()) {
      setError('Please enter a code');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Validate if validator provided
      if (validateManualCode) {
        const validation = await validateManualCode(manualCodeInput.trim());
        if (!validation.valid) {
          setError(validation.error || 'Invalid code');
          setLoading(false);
          return;
        }
      }
      
      // Call success callback with manual code
      await onScanSuccess?.(manualCodeInput.trim());
      
      setSuccess(true);
      setLoading(false);
      
      // Auto-close after brief delay
      setTimeout(() => {
        handleClose();
      }, 1500);
      
    } catch (err) {
      console.error('[QRCodeScanner] Error processing manual code:', err);
      setError(err.message || 'Failed to process code');
      setLoading(false);
      
      onScanError?.(err);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    stopCamera();
    setError(null);
    setSuccess(false);
    setManualCodeInput('');
    setActiveTab(0);
    onClose?.();
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    if (newValue !== 0) {
      stopCamera(); // Stop camera when switching away from camera tab
    }
    setActiveTab(newValue);
    setError(null);
  };

  // Paste from clipboard
  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setManualCodeInput(text);
    } catch (err) {
      console.error('[QRCodeScanner] Failed to read clipboard:', err);
      setError('Failed to read clipboard. Please paste manually.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 500,
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QrCodeScanner color="primary" />
            <Typography variant="h6" component="span">
              {title}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} centered>
          <Tab 
            icon={<CameraAlt />} 
            label="Camera" 
            iconPosition="start"
            disabled={!cameraSupported}
          />
          {allowManualEntry && (
            <Tab 
              icon={<Keyboard />} 
              label="Manual Entry" 
              iconPosition="start"
            />
          )}
          {isDevMode && devModeContent && (
            <Tab 
              icon={<ErrorOutline />} 
              label="Dev Mode" 
              iconPosition="start"
            />
          )}
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 3, pb: 3 }}>
        {/* Camera Tab */}
        {activeTab === 0 && (
          <Box>
            {!cameraSupported ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                Camera not supported on this device. Please use manual entry.
              </Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                  {description}
                </Typography>

                {/* Scanner Container */}
                <Paper
                  elevation={0}
                  sx={{
                    width: '100%',
                    minHeight: 400, // Increased from 300 for better QR detection
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.100',
                    borderRadius: 2,
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <div 
                    id={QR_SCANNER_ELEMENT_ID} 
                    style={{ 
                      width: '100%',
                      minHeight: 400, // Increased from 300
                    }} 
                  />
                  
                  {/* Loading Overlay */}
                  {loading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        zIndex: 10,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )}

                  {/* Success Overlay */}
                  {success && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(76, 175, 80, 0.95)',
                        zIndex: 10,
                      }}
                    >
                      <CheckCircle sx={{ fontSize: 64, color: 'white', mb: 2 }} />
                      <Typography variant="h6" color="white">
                        Success!
                      </Typography>
                    </Box>
                  )}
                </Paper>

                {/* Error Alert */}
                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ mt: 2 }}
                    action={
                      <IconButton
                        size="small"
                        color="inherit"
                        onClick={() => {
                          setError(null);
                          if (!scanning && activeTab === 0) {
                            startCamera();
                          }
                        }}
                      >
                        <Refresh />
                      </IconButton>
                    }
                  >
                    {error}
                  </Alert>
                )}

                {/* Camera Controls */}
                {!scanning && !success && !loading && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Button
                      variant="contained"
                      startIcon={<CameraAlt />}
                      onClick={startCamera}
                    >
                      Start Camera
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* Manual Entry Tab */}
        {activeTab === 1 && allowManualEntry && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              If the camera isn't working, you can enter the code manually.
            </Typography>

            <TextField
              fullWidth
              label={manualEntryLabel}
              value={manualCodeInput}
              onChange={(e) => setManualCodeInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleManualCodeSubmit();
                }
              }}
              disabled={loading || success}
              placeholder="Enter code here"
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={handlePasteFromClipboard}
                    disabled={loading || success}
                    size="small"
                  >
                    <ContentPaste />
                  </IconButton>
                ),
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
                Code validated successfully!
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={handleManualCodeSubmit}
              disabled={loading || success || !manualCodeInput.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
            >
              {loading ? 'Validating...' : 'Submit Code'}
            </Button>
          </Box>
        )}

        {/* Dev Mode Tab */}
        {activeTab === 2 && isDevMode && devModeContent && (
          <Box>
            {devModeContent}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QRCodeScanner;

